import { useRef } from 'react';
import { MULTI_PRODUCT_FILTER_CONSTANTS } from '#client/components/features/MultiProductFilter/constants/multiProductFilterConstants';
import { toChains } from '#shared/ruleGroups';

/**
 *
 * @param {*} user - user object
 * @param {*} permissionName - Name of permission
 * @description Checks if a user has the assigned permission
 */
export function hasPermission(user, permissionName) {
    if (!user?.role) return false;
    return Boolean(user.role[permissionName]);
}

export function requiresLocation(user, permissionName) {
    const locationBasedPermissions = ['poView', 'transferView', 'poUpdate', 'ccView', 'ccCreatePrescribe', 'ccCreateAdHoc', 'ccStart', 'ccUpdate', 'ccManagerApprove', 'ccFinanceApprove'];
    if (user?.locations?.length === 0 && locationBasedPermissions.includes(permissionName)) {
        return false;
    }
    return true;
}

/**
 * Does the user have permission for a specific location
 * @param {*} user - user object
 * @param {*} locationId - ID of the location to check
 * @returns {boolean} - true if user has permission for the location, false otherwise
 */
export function hasPermissionForLocation(user, locationId) {
    if (!user?.role) return false;

    // Locations may not have loaded yet, so deny rather than throwing
    if (!Array.isArray(user.locationIds)) return false;

    // Check if user has access to the specific location
    return user.locationIds.includes(locationId);
}

/**
 *
 * @param {*} dateString
 * @description Formats a date string into MM/DD/YYYY format.
 */
export const formatDate = (dateString) => {
    if (!dateString) return null;
    // Cast the dateString to a Date object, with it being of type "object"
    const date = new Date(dateString);

    // An invalid Date is still an object, so check the timestamp rather than the type
    if (isNaN(date.getTime())) return null;

    // updates format of date depending on your locale
    return date.toLocaleDateString();
};

// Formats a date string into MM/DD/YYYY format in UTC
export const formatDateToUTC = (dateString) => {
    if (!dateString) return null;
    // Cast the dateString to a Date object, with it being of type "object"
    const date = new Date(dateString);

    // An invalid Date is still an object, so check the timestamp rather than the type
    if (isNaN(date.getTime())) return null;

    const month = String(date.getUTCMonth() + 1);
    const day = String(date.getUTCDate());
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
};

/**
 *
 * @param {*} dateString
 * @description Formats a date string into MM/DD/YYYY, HH:MM:SS PM/AM format.
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return null;

    // Cast the dateString to a Date object, with it being of type "object"
    const date = new Date(dateString);

    // An invalid Date is still an object, so check the timestamp rather than the type
    if (isNaN(date.getTime())) return null;

    // updates format of date depending on your locale
    const dateStr = date.toLocaleDateString([], {
        year: 'numeric', // 'numeric' for full year
        month: '2-digit', // '2-digit' for month
        day: '2-digit', // '2-digit' for day
    });

    // updates format of time depending on your locale
    const timeStr = date.toLocaleTimeString([], {
        hour: 'numeric', // 'numeric' for 12-hour format
        minute: '2-digit', // '2-digit' for minutes
        hour12: true, // true for 12-hour format with AM/PM
    });

    // Return the formatted date and time
    return `${dateStr} ${timeStr}`;
};

/**
 *
 * @param {*} rows - Array of rows to be sorted
 * @param {*} setSortedRows - Function to set the sorted rows
 * @param {*} index - Column index to sort by
 * @param {*} direction - Sort direction (ascending or descending)
 * @description Sorts the rows based on the specified column index and direction.
 */
export const handleSort = (rows, columnIndex, direction, columnKeyMap) => {
    const key = columnKeyMap[columnIndex];
    if (!key) return rows;

    const sorted = [...rows]
        .map((row, index) => ({ row, index })) // attach original index for stable sort
        .sort((a, b) => {
            const valueA = a.row[key];
            const valueB = b.row[key];

            // Handle null/undefined
            if (valueA == null) return 1;
            if (valueB == null) return -1;

            // Handle numeric
            if (!isNaN(valueA) && !isNaN(valueB)) {
                return direction === 'ascending' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
            }

            // Handle dates
            const dateA = new Date(valueA);
            const dateB = new Date(valueB);
            if (!isNaN(dateA) && !isNaN(dateB)) {
                return direction === 'ascending' ? dateA - dateB : dateB - dateA;
            }

            // Fallback to string
            if (valueA < valueB) return direction === 'ascending' ? -1 : 1;
            if (valueA > valueB) return direction === 'ascending' ? 1 : -1;

            // Stable sort
            return a.index - b.index;
        })
        .map(({ row }) => row);

    return sorted;
};

/**
 *
 * @param {*} rulesToParse - String of rules to parse
 * @param {*} conditionType - Condition type to use
 * @description Parses the saved rules and returns the filters and condition type.
 */
export const parseSavedRules = (rulesToParse, conditionType) => {
    const hasAnd = typeof rulesToParse === 'string' && rulesToParse.includes(' AND ');
    const hasOr = typeof rulesToParse === 'string' && rulesToParse.includes(' OR ');

    const condition = hasAnd ? 'allConditions' : hasOr ? 'anyConditions' : conditionType || 'allConditions';

    if (typeof rulesToParse !== 'string') {
        return { filters: rulesToParse, condition };
    }

    // First, extract grouped rules (in parentheses) and replace with placeholders
    const groupedRules = [];
    let workingString = rulesToParse;
    const groupRegex = /\(([^)]+)\)/g;
    let match;

    while ((match = groupRegex.exec(rulesToParse)) !== null) {
        const groupContent = match[1];
        const placeholder = `__GROUP_${groupedRules.length}__`;
        groupedRules.push(groupContent);
        workingString = workingString.replace(match[0], placeholder);
    }

    // Split by AND/OR to get individual rule segments
    const ruleStrings = workingString.split(/\s+(?:AND|OR)\s+/);

    const filters = ruleStrings.map((rule) => {
        // Check if this is a grouped placeholder
        if (rule.startsWith('__GROUP_')) {
            const groupIndex = parseInt(rule.match(/__GROUP_(\d+)__/)[1]);
            const groupContent = groupedRules[groupIndex];

            // Parse the grouped content (should be ORed same-field filters)
            const groupParts = groupContent.split(/\s+OR\s+/);
            const parsedGroup = groupParts.map((part) => parseRule(part));

            // All parts should have the same field and condition
            if (parsedGroup.length > 0) {
                const firstRule = parsedGroup[0];
                const result = {
                    attribute: firstRule.attribute,
                    condition: firstRule.condition,
                    value: parsedGroup.map((r) => r.value),
                };

                // Aggregate categoryNames from grouped rules
                const categoryNames = parsedGroup.map((r) => r.categoryName).filter(Boolean);
                if (categoryNames.length > 0) {
                    result.categoryName = categoryNames;
                }

                return result;
            }
        }

        // Regular single rule
        return parseRule(rule);
    });

    return { filters, condition };
};

// Helper function to parse a single rule
function parseRule(rule) {
    // Split only on first colon to preserve pipe in category values like "id|Name"
    const colonIndex = rule.indexOf(':');
    let field = colonIndex !== -1 ? rule.slice(0, colonIndex) : rule;
    let value = colonIndex !== -1 ? rule.slice(colonIndex + 1) : '';
    let condition = 'EQUALS';
    let isNegated = false;

    // Check for negation in field
    if (field && field.startsWith('-')) {
        isNegated = true;
        field = field.slice(1);
    }

    if (field.includes('metafields.')) {
        const [, namespace, key] = field.split('.');
        return {
            attribute: `metafields.${namespace}.${key}`,
            condition: isNegated ? 'NOT_EQUALS' : 'EQUALS',
            value: value?.replace(/^['"]|['"]$/g, ''),
        };
    }

    // Check for comparison operators in field
    if (field) {
        if (field.startsWith('>')) {
            condition = 'GREATER_THAN';
            field = field.slice(1);
        } else if (field.startsWith('<')) {
            condition = 'LESS_THAN';
            field = field.slice(1);
        }
    }

    // Check for wildcards in value to determine string matching conditions
    if (value) {
        if (value === 'isNull') {
            condition = 'IS_NULL';
            value = '-';
        } else if (value.startsWith('*') && value.endsWith('*')) {
            condition = isNegated ? 'NOT_CONTAINS' : 'CONTAINS';
            value = value.slice(1, -1);
        } else if (value.startsWith('*')) {
            condition = isNegated ? 'NOT_EQUALS' : 'ENDS_WITH';
            value = value.slice(1);
        } else if (value.endsWith('*')) {
            condition = isNegated ? 'NOT_EQUALS' : 'STARTS_WITH';
            value = value.slice(0, -1);
        } else if (value.startsWith('>')) {
            condition = 'GREATER_THAN';
            value = value.slice(1);
        } else if (value.startsWith('<')) {
            condition = 'LESS_THAN';
            value = value.slice(1);
        } else if (isNegated && condition === 'EQUALS') {
            // No wildcards but negated
            condition = 'NOT_EQUALS';
        }
    }

    // For category_id, extract name if present (format: id|Name)
    let categoryName = null;
    let cleanValue = value?.replace(/^['"]|['"]$/g, '') || '';

    if ((field === 'category_id' || field === '-category_id') && cleanValue.includes('|')) {
        const [categoryId, name] = cleanValue.split('|');
        cleanValue = `${categoryId}`;
        categoryName = name;
    }

    const result = {
        attribute: field?.replace(/^-/, '') || 'title', // Remove negation prefix from attribute
        condition,
        value: cleanValue,
    };

    // Include categoryName if present (for populating categoryNames state)
    if (categoryName) {
        result.categoryName = categoryName;
    }

    return result;
}

/**
 *
 * @param {*} fieldKey - Field key to map to Shopify's rule field
 * @description Maps a field key to Shopify's rule field.
 */
export const getField = (fieldKey) => {
    if (!fieldKey) return '';
    if (fieldKey.startsWith('metafields')) {
        // Extract namespace and key from the metafield value
        const [, namespace, metafieldKey] = fieldKey.split(/[.]/);
        return `metafields.${namespace}.${metafieldKey}`;
    }
    return fieldKey.toLowerCase(); // Convert standard fields to lowercase
};

/**
 * Builder groups to the API's ruleGroups shape. Each group's rules use the same
 * field/relation/values conversion as a flat search. Category values carry their
 * display name as "id|Name", like the legacy rule string, so they reload with names.
 *
 * @param {Array} groups - builder groups: { name, joiner, joinWithPrevious, isRequired, filters }
 * @returns {Array} ruleGroups for the program and search APIs
 */
export const toApiRuleGroups = (groups) =>
    groups.map((group) => ({
        name: group.name.trim(),
        joiner: group.joiner,
        joinWithPrevious: group.joinWithPrevious,
        isRequired: group.isRequired,
        rules: group.filters.map((filter) => {
            const values = Array.isArray(filter.value) ? filter.value : [filter.condition === 'IS_NULL' ? '-' : String(filter.value ?? '').trim()];
            return {
                field: getField(filter.attribute),
                relation: filter.condition,
                values: filter.attribute === 'category_id' ? values.map((id) => (filter.labels?.[id] ? `${id}|${filter.labels[id]}` : id)) : values,
            };
        }),
    }));

// The part of API rule groups that decides which products match; names and Required/Optional don't
export const ruleGroupsQuery = (groups = []) =>
    JSON.stringify(groups.map(({ joiner, joinWithPrevious, rules }) => ({ joiner, joinWithPrevious, rules: rules.map(({ field, relation, values }) => ({ field, relation, values })) })));

// The part of All Conditions or Any Conditions rules that decides which products match
const filtersQuery = (filters = []) => JSON.stringify(filters.map(({ attribute, condition, value }) => ({ attribute, condition, value })));

/**
 * A program saves the product list from its last search, so a save waits for Search Products after
 * Products must match changes or a rule changes. Renaming a group or changing Required/Optional
 * needs no search.
 *
 * @param {Object} state - the product filter store's state
 * @returns {string|undefined} the reason the save has to wait, or undefined when it can go ahead
 */
export const unsearchedRulesError = ({ restrictToEligibleProducts, conditionType, searchedConditionType, ruleGroups, ruleGroupsPayload, filters, searchedFilters }) => {
    if (!restrictToEligibleProducts) return undefined;
    const rulesChanged =
        conditionType === 'groupedRules' ? ruleGroupsQuery(toApiRuleGroups(ruleGroups)) !== ruleGroupsQuery(ruleGroupsPayload ?? []) : filtersQuery(filters) !== filtersQuery(searchedFilters);
    if (conditionType === searchedConditionType && !rulesChanged) return undefined;
    return 'Click Search Products before saving, so the saved products match the rules.';
};

/**
 * The API's ruleGroups back into builder groups, the reverse of toApiRuleGroups().
 *
 * @param {Array} ruleGroups - ruleGroups from the program API
 * @returns {Array} builder groups, each with a stable key for rendering
 */
export const toFormRuleGroups = (ruleGroups = []) =>
    ruleGroups.map((group) => ({
        key: crypto.randomUUID(),
        name: group.name,
        joiner: group.joiner,
        joinWithPrevious: group.joinWithPrevious,
        isRequired: group.isRequired,
        filters: group.rules.map((rule) => {
            const attribute = MULTI_PRODUCT_FILTER_CONSTANTS.BASE_PRODUCT_OPTIONS.find((option) => getField(option.value) === rule.field)?.value ?? rule.field;
            const labels = {};
            const values =
                attribute === 'category_id'
                    ? rule.values.map((value) => {
                          const [id, ...name] = value.split('|');
                          if (name.length) labels[id] = name.join('|');
                          return id;
                      })
                    : rule.values;
            return { attribute, condition: rule.relation, value: values.length === 1 ? values[0] : values, labels };
        }),
    }));

// Unnamed groups are shown by their position
const groupLabel = (group, index) => group.name.trim() || `Group ${index + 1}`;

/**
 * Group names by chain for the rule summary: the chains that make a product eligible,
 * and the chains whose items meet the voucher's requirement.
 *
 * @param {Array} groups - builder groups
 * @returns {{ eligibleChains: string[][], requiredChains: string[][] }}
 */
export const summarizeRuleGroups = (groups) => {
    const chains = toChains(groups.map((group, index) => ({ ...group, name: groupLabel(group, index) })));
    const names = (chain) => chain.map((group) => group.name);
    return {
        eligibleChains: chains.map(names),
        requiredChains: chains.filter((chain) => chain.some((group) => group.isRequired)).map(names),
    };
};

/**
 * Groups joined with AND must all be Required or all be Optional, or Required has no effect.
 * Matches the server's validation message.
 *
 * @param {Array} groups - builder groups
 * @returns {Object<string, string>} error message by group key, for each group in a mixed chain
 */
export const mixedRequirementErrors = (groups) => {
    const errors = {};
    const labelled = groups.map((group, index) => ({ ...group, label: groupLabel(group, index) }));
    toChains(labelled).forEach((chain) => {
        if (new Set(chain.map((group) => group.isRequired)).size < 2) return;
        const message = `Groups ${chain.map((group) => `"${group.label}"`).join(', ')} are joined with AND, so they must all be Required or all be Optional`;
        chain.forEach((group) => {
            errors[group.key] = message;
        });
    });
    return errors;
};

/**
 *
 * @param {*} value - Value to map to Shopify's rule field
 * @description Maps a value to Shopify's rule field.
 */
export const fieldChangeRelation = (value) => {
    // Handle metafield attributes
    if (value.startsWith('metafields.')) {
        return [
            { label: 'is equal to', value: 'EQUALS' },
            { label: 'is not equal to', value: 'NOT_EQUALS' },
        ];
    }

    // Handle standard product attributes
    switch (value) {
        case 'category_id':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        case 'compareAtPrice':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'is greater than', value: 'GREATER_THAN' },
                { label: 'is less than', value: 'LESS_THAN' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        case 'inventory_total':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is greater than', value: 'GREATER_THAN' },
                { label: 'is less than', value: 'LESS_THAN' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        case 'price':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'is greater than', value: 'GREATER_THAN' },
                { label: 'is less than', value: 'LESS_THAN' },
            ];
        case 'product_type':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'contains', value: 'CONTAINS' },
                { label: 'does not contain', value: 'NOT_CONTAINS' },
                { label: 'starts with', value: 'STARTS_WITH' },
                { label: 'ends with', value: 'ENDS_WITH' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        case 'sku':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'contains', value: 'CONTAINS' },
                { label: 'does not contain', value: 'NOT_CONTAINS' },
                { label: 'starts with', value: 'STARTS_WITH' },
                { label: 'ends with', value: 'ENDS_WITH' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        case 'tag':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        case 'vendor':
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'contains', value: 'CONTAINS' },
                { label: 'does not contain', value: 'NOT_CONTAINS' },
                { label: 'starts with', value: 'STARTS_WITH' },
                { label: 'ends with', value: 'ENDS_WITH' },
                { label: 'Is Null', value: 'IS_NULL' },
            ];
        default:
            return [
                { label: 'is equal to', value: 'EQUALS' },
                { label: 'is not equal to', value: 'NOT_EQUALS' },
                { label: 'contains', value: 'CONTAINS' },
                { label: 'does not contain', value: 'NOT_CONTAINS' },
                { label: 'starts with', value: 'STARTS_WITH' },
                { label: 'ends with', value: 'ENDS_WITH' },
            ];
    }
};

/**
 *
 * @param {*} rule - Rule to build the query fragment for
 * @description Builds the query string segment for a single rule.
 */
export const buildQueryFragment = (rule) => {
    if (!rule || !rule.field || !rule.value) {
        return '';
    }

    const key = rule.field;
    const value = rule.value.trim();

    if (!value) {
        return '';
    }

    // Helper function to format the value
    const formatValue = (val) => {
        // If it's a number, return it as is
        if (!isNaN(Number(val)) && val !== '') {
            return val;
        }

        // If is category ID with name (format: gid://...TaxonomyCategory/id|Name), preserve id|name
        if (val.startsWith('gid://shopify/TaxonomyCategory/')) {
            const lastPart = val.split('/').pop(); // Extract "id" or "id|Name"
            return lastPart; // Preserve the id|name format if present
        }

        // If it's a string, remove any quotes and return as is
        return val.replace(/^'|'$/g, '').replace(/^"|"$/g, '');
    };

    let queryFragment;
    switch (rule.relation) {
        case 'EQUALS':
            queryFragment = `${key}:${formatValue(value)}`;
            break;
        case 'NOT_EQUALS':
            queryFragment = `-${key}:${formatValue(value)}`;
            break;
        case 'CONTAINS':
            queryFragment = `${key}:*${formatValue(value)}*`;
            break;
        case 'NOT_CONTAINS':
            queryFragment = `-${key}:*${formatValue(value)}*`;
            break;
        case 'STARTS_WITH':
            queryFragment = `${key}:${formatValue(value)}*`;
            break;
        case 'ENDS_WITH':
            queryFragment = `${key}:*${formatValue(value)}`;
            break;
        case 'GREATER_THAN':
            queryFragment = `${key}:>${value}`; // Numbers don't need quotes for comparisons
            break;
        case 'LESS_THAN':
            queryFragment = `${key}:<${value}`; // Numbers don't need quotes for comparisons
            break;
        case 'IS_NULL':
            queryFragment = `${key}:isNull`;
            break;
        default:
            queryFragment = `${key}:${formatValue(value)}`;
    }

    return queryFragment;
};

/**
 *
 * @param {*} error - Zod error object or array
 * @description Formats Zod errors for client-side use
 */
export const formatZodErrors = (error) => {
    if (!error) return {};

    if (Array.isArray(error)) {
        const fieldErrors = {};
        error.forEach((err) => {
            const field = err.path.join('.');
            fieldErrors[field] = err.message;
        });
        return fieldErrors;
    }

    return {};
};

/**
 *
 * @param {*} callback - Callback function to debounce
 * @param {*} delay - Delay in milliseconds
 * @description Debounces a callback function to avoid race conditions.
 */
export const useDebouncedCallback = (callback, delay) => {
    const timer = useRef();

    return (...args) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            callback(...args);
        }, delay);
    };
};

/**
 *
 * @description Scrolls to the first error element in the document.
 */
export const scrollToFirstError = () => {
    setTimeout(() => {
        const firstErrorElement = document.querySelector('.Polaris-InlineError');
        if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, 100);
};

/**
 *
 * @param {Date} date - Date to normalize
 * @description Normalizes a date to midnight (00:00:00) in local timezone
 * @returns {Date} - Date set to midnight
 */
export const normalizeToMidnight = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

/**
 * Renders a stored phone number as (555) 555-5555, leaving anything that is not
 * a ten digit US number as it was given.
 */
export function formatPhoneNumber(phone) {
    if (!phone) return '';

    const digits = phone.replace(/\D/g, '');

    const cleaned = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

    if (cleaned.length !== 10) return phone;

    const area = cleaned.slice(0, 3);
    const prefix = cleaned.slice(3, 6);
    const line = cleaned.slice(6);

    return `(${area}) ${prefix}-${line}`;
}
